/**
 * Dashboard 위젯 설정 — 기간 / 데이터 선택 / 차트 스타일.
 *
 * Phase A 범위:
 *   - WidgetSettings 스키마 + 헬퍼
 *   - WidgetSettingsModal (ScrollspyModal 기반 3섹션)
 *   - Period 필터는 LineChart(원재료비)에서 즉시 적용
 *   - Data / Style은 UI 완성, 적용은 차트별 점진 wire
 *
 * 데이터 분류 — 위젯 type별로 다른 taxonomy 사용 (data-taxonomies.ts에 모음).
 */
import type { ChartStylePreset, ChartYAxisMode } from "@/components/charts";
import { useMemo, useState } from "react";
import { ScrollspyModal } from "@/components/ui/ScrollspyModal";
import { Icon } from "@/components/ui/Icon";
import { Select } from "@/components/ui/Select";
import type { MaterialCategory, MaterialCategoryKind } from "@/domain/material";

// ─────────────────────────────────────────────────────────────
// 타입 + 상수
// ─────────────────────────────────────────────────────────────

export type WidgetPeriod = "year" | "1m" | "3m" | "6m" | "12m";

export const PERIOD_OPTIONS: { key: WidgetPeriod; label: string; desc: string }[] = [
  { key: "year", label: "당해연도 전체", desc: "1~12월 (기본)" },
  { key: "1m",   label: "최근 1개월",   desc: "이번 달" },
  { key: "3m",   label: "최근 3개월",   desc: "분기 단위" },
  { key: "6m",   label: "최근 6개월",   desc: "반기 단위" },
  { key: "12m",  label: "최근 12개월",  desc: "연 단위 rolling" },
];

export type WidgetStylePreset = ChartStylePreset;

export const STYLE_PRESETS: {
  key: WidgetStylePreset;
  /** 업무 중심 라벨 (사용자 노출) */
  label: string;
  /** 기술명 (subtitle) */
  techName: string;
  desc: string;
  /** 차트 type 호환성 — 미지정 시 모든 차트에 적용 가능 */
  compat?: ("line" | "bar" | "bar-combo")[];
}[] = [
  { key: "basic-line",  label: "추세",         techName: "Line",       desc: "월별 흐름을 한눈에",         compat: ["line"] },
  { key: "smooth-line", label: "부드러운 추세", techName: "Smooth",     desc: "곡선으로 자연스러운 변화",   compat: ["line"] },
  { key: "area-line",   label: "누적 흐름",    techName: "Area",       desc: "선 아래 영역 강조",         compat: ["line"] },
  { key: "bar",         label: "월별 비교",    techName: "Bar",        desc: "기간별 막대 비교",          compat: ["line", "bar"] },
  { key: "mixed",       label: "비교 + 합계",  techName: "Mixed",      desc: "막대 + 합계 라인",          compat: ["bar-combo"] },
  { key: "minimal",     label: "요약형",       techName: "Minimal",    desc: "축/범례 최소화, 카드 적합",  compat: ["line", "bar"] },
];

export type YAxisMode = ChartYAxisMode;

export const Y_AXIS_OPTIONS: { key: YAxisMode; label: string; desc: string; advanced?: boolean }[] = [
  { key: "auto",      label: "자동",          desc: "데이터에 맞춰 최적화 (추천)" },
  { key: "single",    label: "단일 축",       desc: "한 y축에 모두 표시", advanced: true },
  { key: "dual",      label: "듀얼 축",       desc: "큰 값/작은 값 좌·우로 분리", advanced: true },
  { key: "normalize", label: "정규화 (100)",  desc: "첫 값을 100 기준으로 변화율 비교", advanced: true },
];

/**
 * WidgetSettings — 위젯별 설정.
 * 개념적으로 5개 영역으로 분류 (UI 섹션과 매핑):
 *   · data         : period, dataPaths
 *   · visualization: stylePreset, yAxisMode, axisAssignment
 *   · interaction  : focusEnabled, autoSuggestEnabled
 *   · style        : (향후 — 색상 팔레트 / 폰트 등)
 *   · insight      : (향후 — 자동 인사이트 badge / threshold)
 *
 * 구조는 backward-compat 유지 위해 flat. 신규 필드 추가 시 위 분류 따라 코멘트 표시.
 */
export interface WidgetSettings {
  // ── data ─────────────────────────────────────────
  /** 기간 — 기본 "year" */
  period?: WidgetPeriod;
  /**
   * 선택된 데이터 path 집합. 빈 배열 = 위젯 기본값.
   * 4-level path 표준 (대.중.소.metric):
   *   · LineChart 원자재: "raw.{kind}.{code}.price"  (legacy 3-level "raw.{kind}.{code}"도 호환)
   *   · BarChart 출하  : "shipment.qty.{tier}.volume"  (legacy "shipment.qty.{tier}"도 호환)
   * 향후 확장: shipment.price.tier.amount / vehicle.usage.cat.runs 등.
   */
  dataPaths?: string[];

  // ── visualization ────────────────────────────────
  /** 차트 스타일 프리셋. 미지정 = 차트 type 기본 */
  stylePreset?: WidgetStylePreset;
  /** Y축 모드 — 기본 "auto" (single + 자동 추천 배너) */
  yAxisMode?: YAxisMode;
  /** dual 모드에서 series별 축 할당. key=series code, value="left"|"right". 미설정→"left" */
  axisAssignment?: Record<string, "left" | "right">;

  // ── interaction ──────────────────────────────────
  /** 범례 hover/click 시 다른 series dim — 기본 true */
  focusEnabled?: boolean;
  /** Smart recommendation 패널 표시 — 기본 true */
  autoSuggestEnabled?: boolean;

  // ── insight (향후 확장) ───────────────────────────
  /** 자동 인사이트 badge enabled — 향후 구현 */
  insightEnabled?: boolean;

  // ── KPI 전용 (type==="kpi" 위젯에서만 사용) ─────────
  /** delta(증감) 비교 기준 — 기본 "prev_month" */
  kpiCompareBasis?: KpiCompareBasis;
  /** sparkline 표시 여부 — 기본 true */
  kpiShowSpark?: boolean;
  /**
   * KPI 차원(단일 선택). kpiIndex 별 의미가 다름:
   *   - 출하/매출/평균단가: "total" | "gov" | "cust"
   *   - 가동차량(fleet)   : "all" | "own" | "lease" | "outsourced"
   * 기본: "total" (fleet 은 "all").
   */
  kpiDimension?: string;
  /**
   * 평균단가 KPI 전용 — 특정 규격(mixSpec, 예 "25-21-150")만 집계.
   * "" 또는 undefined = 전체 규격 (기존 동작). kpiDimension(관급/사급/합계)과 함께 적용.
   */
  kpiPriceSpec?: string;
  /** 매출 KPI — VAT 포함(합계금액) vs 제외(공급가). 기본 true(포함) */
  kpiRevIncludeVat?: boolean;
  /** 목표값 (price/fleet 등). 0/undefined = 미사용 */
  kpiTarget?: number;

  // ── 캘린더 전용 (type==="calendar" 위젯에서만 사용) ─────────
  /** 출하 있는 날 날짜에 원 + 물량 표시 — 기본 true */
  calShowShipments?: boolean;
  /** 직원 생일 마커 표시 — 기본 false (추후 상세 예정) */
  calShowBirthdays?: boolean;
}

// ─────────────────────────────────────────────────────────────
// KPI 설정 — 차원 / 비교기준 옵션 catalog
// ─────────────────────────────────────────────────────────────

export type KpiCompareBasis = "prev_month" | "prev_year";

export const KPI_COMPARE_OPTIONS: { value: KpiCompareBasis; label: string }[] = [
  { value: "prev_month", label: "전월 대비" },
  { value: "prev_year", label: "전년 동월 대비" },
];

/** KPI 위젯 종류 (kpiIndex) → 차원 옵션 */
export const KPI_DIMENSION_OPTIONS: Record<number, { value: string; label: string }[]> = {
  0: [ // 출하량
    { value: "total", label: "합계" },
    { value: "gov", label: "관급" },
    { value: "cust", label: "사급" },
  ],
  1: [ // 매출
    { value: "total", label: "합계" },
    { value: "gov", label: "관급" },
    { value: "cust", label: "사급" },
  ],
  2: [ // 평균단가
    { value: "total", label: "전체" },
    { value: "gov", label: "관급" },
    { value: "cust", label: "사급" },
  ],
  3: [ // 가동차량
    { value: "all", label: "전체" },
    { value: "own", label: "자차" },
    { value: "lease", label: "지입차" },
    { value: "outsourced", label: "용차" },
  ],
};

/** kpiIndex 별 기본 차원값 */
export function defaultKpiDimension(kpiIndex: number): string {
  return kpiIndex === 3 ? "all" : "total";
}

export const DEFAULT_SETTINGS: Required<
  Omit<
    WidgetSettings,
    | "insightEnabled"
    | "kpiCompareBasis"
    | "kpiShowSpark"
    | "kpiDimension"
    | "kpiPriceSpec"
    | "kpiRevIncludeVat"
    | "kpiTarget"
    | "calShowShipments"
    | "calShowBirthdays"
  >
> = {
  period: "year",
  dataPaths: [],
  stylePreset: "basic-line",
  yAxisMode: "auto",
  axisAssignment: {},
  focusEnabled: true,
  autoSuggestEnabled: true,
};

// ─────────────────────────────────────────────────────────────
// Smart Recommendation — series 특성 분석 → 추천 표시
// ─────────────────────────────────────────────────────────────

export type RecommendationKind =
  | "use-dual"          // 값 차이 큼 → 듀얼 축
  | "use-normalize"     // 변화율 비교가 적합 → 정규화
  | "use-line"          // dataset 수 많음 → line이 비교 적합
  | "use-area"          // 누적형 → area
  | "use-bar";          // 적은 dataset + 비교 강조 → bar

export interface Recommendation {
  kind: RecommendationKind;
  /** 사용자 노출 메시지 */
  message: string;
  /** 적용 액션 (있으면 "적용" 버튼 표시) */
  apply?: {
    label: string;
    onApply: () => void;
  };
}

/** 시리즈 통계 (recommendation 계산에 사용) */
export interface SeriesStats {
  /** 시리즈 개수 */
  count: number;
  /** 시리즈별 평균 값 */
  avgs: number[];
  /** 시리즈별 표준편차 / 평균 (CV) */
  cvs: number[];
  /** 모든 시리즈가 시간에 따라 증가만 하는가 */
  monotonicAll: boolean;
}

export function computeSeriesStats(seriesVals: (number | null)[][]): SeriesStats {
  const avgs: number[] = [];
  const cvs: number[] = [];
  let monotonicAll = true;
  for (const vals of seriesVals) {
    const nums = vals.filter((v): v is number => v != null);
    if (nums.length === 0) {
      avgs.push(0);
      cvs.push(0);
      continue;
    }
    const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
    avgs.push(avg);
    const variance = nums.reduce((a, b) => a + (b - avg) ** 2, 0) / nums.length;
    cvs.push(avg > 0 ? Math.sqrt(variance) / avg : 0);
    // monotonic 검사 — 한 series라도 떨어진 적 있으면 false
    for (let i = 1; i < nums.length; i++) {
      if (nums[i] < nums[i - 1]) {
        monotonicAll = false;
        break;
      }
    }
  }
  return { count: seriesVals.length, avgs, cvs, monotonicAll };
}

export function recommend(
  stats: SeriesStats,
  current: { stylePreset?: WidgetStylePreset; yAxisMode?: YAxisMode },
  actions: {
    setYAxisMode: (m: YAxisMode) => void;
    setStylePreset: (p: WidgetStylePreset) => void;
  },
): Recommendation[] {
  const out: Recommendation[] = [];
  const posAvgs = stats.avgs.filter((a) => a > 0);
  const ratio = posAvgs.length >= 2 ? Math.max(...posAvgs) / Math.min(...posAvgs) : 1;

  // 1. 값차 큼 + 현재 단일/자동 → 듀얼 추천
  if (ratio >= 5 && (current.yAxisMode === "auto" || current.yAxisMode === "single" || !current.yAxisMode)) {
    out.push({
      kind: "use-dual",
      message: `값 차이가 약 ${ratio.toFixed(1)}배 입니다. 듀얼 축으로 분리하면 작은 값 변화도 명확히 보입니다.`,
      apply: { label: "듀얼 축으로", onApply: () => actions.setYAxisMode("dual") },
    });
  }

  // 2. 변화율 비교 적합 — CV 차이가 비슷한데 절대값 차이 큼 → normalize 추천
  if (ratio >= 3 && stats.cvs.length >= 2) {
    const cvSpread = Math.max(...stats.cvs) - Math.min(...stats.cvs);
    if (cvSpread < 0.3) {
      out.push({
        kind: "use-normalize",
        message: "각 데이터의 변화 패턴이 비슷합니다. 정규화 모드로 변화율을 직접 비교할 수 있습니다.",
        apply: { label: "정규화 모드로", onApply: () => actions.setYAxisMode("normalize") },
      });
    }
  }

  // 3. 누적형 (모든 series 단조 증가) → area 추천
  if (stats.monotonicAll && stats.count >= 1 && current.stylePreset !== "area-line") {
    out.push({
      kind: "use-area",
      message: "데이터가 계속 누적되는 형태입니다. 누적 흐름(Area) 차트가 적합합니다.",
      apply: { label: "누적 흐름으로", onApply: () => actions.setStylePreset("area-line") },
    });
  }

  // 4. dataset 많음 → line이 비교 적합
  if (stats.count >= 4 && current.stylePreset === "bar") {
    out.push({
      kind: "use-line",
      message: `데이터 수가 ${stats.count}개로 많습니다. 추세(Line)로 보면 겹치지 않고 비교가 쉽습니다.`,
      apply: { label: "추세 차트로", onApply: () => actions.setStylePreset("basic-line") },
    });
  }

  return out;
}

/** Insight type catalog — 향후 자동 인사이트 badge 구현용 */
export const INSIGHT_TYPES = [
  { key: "max-recent",    label: "최근 최고치",   desc: "선택 기간 중 가장 높은 값 도달" },
  { key: "mom-change",    label: "전월 대비 급등/급락", desc: "전월 대비 ±20% 이상 변동" },
  { key: "mean-deviation",label: "평균 대비 이탈",  desc: "장기 평균에서 표준편차 이상 벗어남" },
  { key: "volatility",    label: "변동성 높음",     desc: "최근 N개월 변동계수 임계 초과" },
] as const;

// ─────────────────────────────────────────────────────────────
// Period 헬퍼 — 12 entry 배열에서 최근 N월 추출
// ─────────────────────────────────────────────────────────────

/** 현재 KST 월 (1~12) */
export function currentMonthKst(): number {
  return new Date().getMonth() + 1;
}

/** monthly 12개 → period에 맞는 (startIdx, endIdx) 반환 (inclusive, 0-based) */
export function periodSlice(period: WidgetPeriod = "year"): { startIdx: number; endIdx: number } {
  const m = currentMonthKst(); // 1~12
  const endIdx = m - 1; // 현재 월의 0-based 인덱스
  if (period === "year") return { startIdx: 0, endIdx: 11 };
  const n =
    period === "1m" ? 1 :
    period === "3m" ? 3 :
    period === "6m" ? 6 : 12;
  const startIdx = Math.max(0, endIdx - n + 1);
  return { startIdx, endIdx };
}

// ─────────────────────────────────────────────────────────────
// 데이터 분류 — 위젯별 taxonomy
// ─────────────────────────────────────────────────────────────

/**
 * DataTaxonomy — 위젯별 데이터 선택 구조.
 *
 * 4-level 표준 (대분류 → 중분류 → 소분류 → metric):
 *   · 대분류 (categoryKey)  : "raw" / "shipment" / "vehicle" / "sales" / "production"
 *   · 중분류 (subKey)       : 카테고리 안의 묶음 (예: 자재 종류 / 출하 metric 종류)
 *   · 소분류 (DataNode.path)의 path 3번째 segment : 실제 선택 단위 (예: 자재 code / tier)
 *   · metric (path 4번째)   : 단가/물량/비율/재고/평균 등 (LineChart="price", BarChart="volume")
 *
 * path 형식: "{categoryKey}.{subKey}.{소분류}.{metric}"
 *   예: "raw.결합제.SC1.price" / "shipment.qty.gov.volume"
 *
 * Backward-compat: 기존 3-level path("raw.결합제.SC1")도 데이터 차트에서 그대로 동작
 * (wrapper의 `path.split(".")[2]`가 소분류만 추출하므로).
 */
export interface DataNode {
  /** 풀 path "{cat}.{sub}.{소}.{metric}" 또는 legacy "{cat}.{sub}.{소}" */
  path: string;
  /** 표시 라벨 (소분류 이름) */
  label: string;
}

export interface DataGroup {
  /** 중분류 라벨 */
  subLabel: string;
  /** 중분류 키 (path 구성용) */
  subKey: string;
  /** 소분류 노드들 */
  metrics: DataNode[];
}

export interface DataTaxonomy {
  /** 대분류 라벨 */
  categoryLabel: string;
  /** 대분류 키 */
  categoryKey: string;
  /** 중분류 그룹들 */
  groups: DataGroup[];
}

/**
 * BarChart / BarComboChart (월별 출하) 데이터 taxonomy.
 *   출하 (대) → 합계 (중) → 관급/사급/합계 (소)
 *
 * path 형식: "shipment.qty.{tier}"  — tier: "gov" | "cust" | "total"
 */
export function buildShipmentTaxonomy(): DataTaxonomy {
  return {
    categoryLabel: "출하",
    categoryKey: "shipment",
    groups: [
      {
        subLabel: "출하량",
        subKey: "qty",
        metrics: [
          { path: "shipment.qty.gov",   label: "관급" },
          { path: "shipment.qty.cust",  label: "사급" },
          { path: "shipment.qty.total", label: "합계" },
        ],
      },
    ],
  };
}

/**
 * LineChart (원재료비) 데이터 taxonomy.
 *   원자재 (대) → 결합제/잔골재/굵은골재/혼화제/기타 (중, MaterialCategoryKind)
 *                → 각 활성 자재 (소) — 단가 metric만 현재 지원
 */
export function buildLineChartTaxonomy(cats: MaterialCategory[]): DataTaxonomy {
  const KIND_ORDER: MaterialCategoryKind[] = ["결합제", "잔골재", "굵은골재", "혼화제", "기타"];
  return {
    categoryLabel: "원자재",
    categoryKey: "raw",
    groups: KIND_ORDER.map((kind) => ({
      subLabel: kind,
      subKey: kind,
      metrics: cats
        .filter((c) => c.category === kind && c.is_active !== false)
        .sort(
          (a, b) =>
            (a.sort_order ?? Number.MAX_SAFE_INTEGER) -
              (b.sort_order ?? Number.MAX_SAFE_INTEGER) ||
            a.code.localeCompare(b.code),
        )
        .map((c) => ({
          path: `raw.${kind}.${c.code}`,
          label: c.name,
        })),
    })).filter((g) => g.metrics.length > 0),
  };
}

// ─────────────────────────────────────────────────────────────
// 모달
// ─────────────────────────────────────────────────────────────

export interface WidgetSettingsModalProps {
  open: boolean;
  onClose: () => void;
  widgetType: string;
  widgetLabel: string;
  /** KPI 위젯일 때 종류 (0=출하량,1=매출,2=평균단가,3=가동차량). 차원 옵션 결정 */
  kpiIndex?: number;
  settings: WidgetSettings;
  /** 위젯에 맞는 데이터 taxonomy (없으면 데이터 섹션 비활성) */
  taxonomy?: DataTaxonomy;
  /** 평균단가 KPI 규격 선택지 (정렬된 mixSpec 목록). 미제공 시 규격 선택 숨김. */
  priceSpecs?: string[];
  onSave: (s: WidgetSettings) => void;
  /** 우측 라이브 프리뷰 렌더러. draft settings를 받아 작은 차트를 그려야 함. */
  renderPreview?: (draft: WidgetSettings) => React.ReactNode;
  /** Smart recommendation용 시리즈 통계 — 현재 차트 데이터 기반. 미제공 시 추천 패널 숨김 */
  seriesStats?: SeriesStats;
}

export function WidgetSettingsModal(props: WidgetSettingsModalProps) {
  // 닫으면 편집 폼을 해제하여 다음 열기 때 저장된 설정으로 초기화한다.
  if (!props.open) return null;
  return <WidgetSettingsForm {...props} />;
}

function WidgetSettingsForm({
  open,
  onClose,
  widgetType,
  widgetLabel,
  kpiIndex,
  settings,
  taxonomy,
  priceSpecs,
  onSave,
  renderPreview,
  seriesStats,
}: WidgetSettingsModalProps) {
  const isKpi = widgetType === "kpi";
  const isCalendar = widgetType === "calendar";
  const kpiIdx = kpiIndex ?? 0;

  // 모달 로컬 state — 저장 누르면 부모로 commit
  const [period, setPeriod] = useState<WidgetPeriod>(settings.period ?? "year");
  const [paths, setPaths] = useState<string[]>(settings.dataPaths ?? []);
  const [preset, setPreset] = useState<WidgetStylePreset>(
    settings.stylePreset ?? "basic-line",
  );
  const [yAxisMode, setYAxisMode] = useState<YAxisMode>(settings.yAxisMode ?? "auto");
  const [axisAssignment, setAxisAssignment] = useState<Record<string, "left" | "right">>(
    settings.axisAssignment ?? {},
  );
  const [focusEnabled, setFocusEnabled] = useState<boolean>(settings.focusEnabled ?? true);
  const [autoSuggestEnabled, setAutoSuggestEnabled] = useState<boolean>(
    settings.autoSuggestEnabled ?? true,
  );
  // ── KPI 전용 state ──
  const [kpiCompareBasis, setKpiCompareBasis] = useState<KpiCompareBasis>(
    settings.kpiCompareBasis ?? "prev_month",
  );
  const [kpiShowSpark, setKpiShowSpark] = useState<boolean>(settings.kpiShowSpark !== false);
  const [kpiDimension, setKpiDimension] = useState<string>(
    settings.kpiDimension ?? defaultKpiDimension(kpiIdx),
  );
  const [kpiPriceSpec, setKpiPriceSpec] = useState<string>(settings.kpiPriceSpec ?? "");
  const [kpiRevIncludeVat, setKpiRevIncludeVat] = useState<boolean>(
    settings.kpiRevIncludeVat !== false,
  );
  const [kpiTarget, setKpiTarget] = useState<number>(settings.kpiTarget ?? 0);
  // ── 캘린더 전용 state ──
  const [calShowShipments, setCalShowShipments] = useState<boolean>(
    settings.calShowShipments !== false,
  );
  const [calShowBirthdays, setCalShowBirthdays] = useState<boolean>(
    settings.calShowBirthdays === true,
  );

  const togglePath = (path: string) => {
    setPaths((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path],
    );
  };

  const toggleAllInGroup = (group: DataGroup) => {
    const groupPaths = group.metrics.map((m) => m.path);
    const allOn = groupPaths.every((p) => paths.includes(p));
    setPaths((prev) =>
      allOn
        ? prev.filter((p) => !groupPaths.includes(p))
        : [...new Set([...prev, ...groupPaths])],
    );
  };

  const setAxisFor = (code: string, axis: "left" | "right") => {
    setAxisAssignment((prev) => ({ ...prev, [code]: axis }));
  };

  // dual 모드에서 보여줄 series 후보 codes (선택값 있으면 그것, 없으면 taxonomy 첫 자재 3개)
  const candidateCodes = useMemo<{ code: string; label: string }[]>(() => {
    if (!taxonomy) return [];
    const all = taxonomy.groups.flatMap((g) => g.metrics);
    if (paths.length > 0) {
      return paths
        .map((p) => p.split(".")[2])
        .filter((c): c is string => !!c)
        .map((c) => {
          const node = all.find((m) => m.path.endsWith("." + c));
          return { code: c, label: node?.label ?? c };
        });
    }
    // default 3개 — taxonomy 그룹별 첫 자재
    return taxonomy.groups
      .map((g) => g.metrics[0])
      .filter((m): m is NonNullable<typeof m> => !!m)
      .slice(0, 3)
      .map((m) => ({ code: m.path.split(".")[2], label: m.label }));
  }, [taxonomy, paths]);

  const handleSave = () => {
    if (isCalendar) {
      onSave({
        ...settings,
        calShowShipments,
        calShowBirthdays,
      });
    } else if (isKpi) {
      onSave({
        ...settings,
        period,
        kpiCompareBasis,
        kpiShowSpark,
        kpiDimension,
        kpiPriceSpec,
        kpiRevIncludeVat,
        kpiTarget,
      });
    } else {
      onSave({
        period,
        dataPaths: paths,
        stylePreset: preset,
        yAxisMode,
        axisAssignment,
        focusEnabled,
        autoSuggestEnabled,
      });
    }
    onClose();
  };

  // Smart recommendations — series 통계 + 현재 draft → 추천 액션
  const recommendations: Recommendation[] = useMemo(() => {
    if (!seriesStats || !autoSuggestEnabled) return [];
    return recommend(
      seriesStats,
      { stylePreset: preset, yAxisMode },
      { setYAxisMode, setStylePreset: setPreset },
    );
  }, [seriesStats, autoSuggestEnabled, preset, yAxisMode]);

  const chartSections = [
    {
      id: "data",
      label: "데이터",
      content: (
        <DataSection
          period={period}
          onChangePeriod={setPeriod}
          taxonomy={taxonomy}
          paths={paths}
          onToggle={togglePath}
          onToggleGroup={toggleAllInGroup}
        />
      ),
    },
    {
      id: "visualization",
      label: "시각화",
      content: (
        <VisualizationSection
          value={preset}
          onChange={setPreset}
          widgetType={widgetType}
        />
      ),
    },
    {
      id: "analysis",
      label: "분석",
      content: (
        <AnalysisSection
          mode={yAxisMode}
          onChangeMode={setYAxisMode}
          candidates={candidateCodes}
          assignment={axisAssignment}
          onChangeAxis={setAxisFor}
          recommendations={recommendations}
        />
      ),
    },
    {
      id: "style",
      label: "스타일",
      content: (
        <StyleSection
          focusEnabled={focusEnabled}
          onChangeFocus={setFocusEnabled}
          autoSuggestEnabled={autoSuggestEnabled}
          onChangeAutoSuggest={setAutoSuggestEnabled}
        />
      ),
    },
    {
      id: "insight",
      label: "인사이트",
      content: <InsightSection />,
    },
  ];

  const kpiSections = [
    {
      id: "kpi",
      label: "지표 설정",
      content: (
        <KpiSection
          kpiIndex={kpiIdx}
          period={period}
          onChangePeriod={setPeriod}
          compareBasis={kpiCompareBasis}
          onChangeCompare={setKpiCompareBasis}
          dimension={kpiDimension}
          onChangeDimension={setKpiDimension}
          priceSpec={kpiPriceSpec}
          onChangePriceSpec={setKpiPriceSpec}
          priceSpecs={priceSpecs ?? []}
          revIncludeVat={kpiRevIncludeVat}
          onChangeRevVat={setKpiRevIncludeVat}
          target={kpiTarget}
          onChangeTarget={setKpiTarget}
          showSpark={kpiShowSpark}
          onChangeShowSpark={setKpiShowSpark}
        />
      ),
    },
  ];

  const calendarSections = [
    {
      id: "calendar",
      label: "표시 항목",
      content: (
        <CalendarSection
          showShipments={calShowShipments}
          onChangeShowShipments={setCalShowShipments}
          showBirthdays={calShowBirthdays}
          onChangeShowBirthdays={setCalShowBirthdays}
        />
      ),
    },
  ];

  const sections = isCalendar ? calendarSections : isKpi ? kpiSections : chartSections;

  // 현재 draft settings (preview에 전달용)
  const draftSettings: WidgetSettings = isCalendar
    ? {
        ...settings,
        calShowShipments,
        calShowBirthdays,
      }
    : isKpi
    ? {
        ...settings,
        period,
        kpiCompareBasis,
        kpiShowSpark,
        kpiDimension,
        kpiRevIncludeVat,
        kpiTarget,
      }
    : {
        period,
        dataPaths: paths,
        stylePreset: preset,
        yAxisMode,
        axisAssignment,
        focusEnabled,
        autoSuggestEnabled,
      };

  return (
    <ScrollspyModal
      open={open}
      onClose={onClose}
      title={
        <span>
          위젯 빌더 <span style={{ color: "var(--c-text-muted)", fontWeight: 500, fontSize: "var(--fs-base)" }}>· {widgetLabel}</span>
        </span>
      }
      sections={sections}
      width={1080}
      height="min(720px, 90vh)"
      rightPane={
        renderPreview ? (
          <div className="ws-preview-pane">
            <div className="ws-preview-frame">
              {renderPreview(draftSettings)}
            </div>
          </div>
        ) : undefined
      }
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>취소</button>
          <button type="button" className="btn-primary" onClick={handleSave}>저장</button>
        </>
      }
    />
  );
}

// ─────────────────────────────────────────────────────────────
// 섹션 — 데이터 (기간 + 대→중→소→metric 선택)
// "무엇을 보고 싶은지" 먼저 정의하는 영역.
// ─────────────────────────────────────────────────────────────

function DataSection({
  period,
  onChangePeriod,
  taxonomy,
  paths,
  onToggle,
  onToggleGroup,
}: {
  period: WidgetPeriod;
  onChangePeriod: (v: WidgetPeriod) => void;
  taxonomy?: DataTaxonomy;
  paths: string[];
  onToggle: (path: string) => void;
  onToggleGroup: (group: DataGroup) => void;
}) {
  return (
    <div className="ws-data-block">
      {/* 기간 */}
      <div className="ws-sub-block">
        <div className="ws-sub-title">기간</div>
        <div className="ws-period-grid">
          {PERIOD_OPTIONS.map((opt) => {
            const active = period === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                className={`ws-period-card ${active ? "is-active" : ""}`}
                onClick={() => onChangePeriod(opt.key)}
                aria-pressed={active ? "true" : "false"}
              >
                <div className="ws-period-label">{opt.label}</div>
                <div className="ws-period-desc">{opt.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 항목 선택 */}
      <div className="ws-sub-block">
        <div className="ws-sub-title">표시할 항목</div>
        {taxonomy ? (
          <>
            <div className="ws-data-cat">{taxonomy.categoryLabel}</div>
            {taxonomy.groups.map((g) => {
              const groupPaths = g.metrics.map((m) => m.path);
              const allOn = groupPaths.every((p) => paths.includes(p));
              const someOn = !allOn && groupPaths.some((p) => paths.includes(p));
              return (
                <div key={g.subKey} className="ws-data-group">
                  <div className="ws-data-sub">
                    <label className="ws-data-sub-toggle">
                      <input
                        type="checkbox"
                        checked={allOn}
                        ref={(el) => { if (el) el.indeterminate = someOn; }}
                        onChange={() => onToggleGroup(g)}
                      />
                      <span>{g.subLabel}</span>
                      <span className="ws-data-sub-count">({groupPaths.filter((p) => paths.includes(p)).length}/{groupPaths.length})</span>
                    </label>
                  </div>
                  <div className="ws-data-metrics">
                    {g.metrics.map((m) => {
                      const on = paths.includes(m.path);
                      return (
                        <label key={m.path} className={`ws-data-chip ${on ? "is-on" : ""}`}>
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => onToggle(m.path)}
                          />
                          <span>{m.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <div className="ws-data-hint">
              ※ 선택 항목이 없으면 위젯 기본값이 사용됩니다.
            </div>
          </>
        ) : (
          <EmptyHint text="이 위젯은 데이터 선택을 지원하지 않습니다." />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 섹션 — KPI 전용 (기간 + 비교기준 + 차원 + VAT/목표 + sparkline)
// KPI 위젯은 단일 숫자라 차트 taxonomy/시각화 섹션 대신 이 한 섹션만 노출.
// ─────────────────────────────────────────────────────────────

function KpiSection({
  kpiIndex,
  period,
  onChangePeriod,
  compareBasis,
  onChangeCompare,
  dimension,
  onChangeDimension,
  priceSpec,
  onChangePriceSpec,
  priceSpecs,
  revIncludeVat,
  onChangeRevVat,
  target,
  onChangeTarget,
  showSpark,
  onChangeShowSpark,
}: {
  kpiIndex: number;
  period: WidgetPeriod;
  onChangePeriod: (v: WidgetPeriod) => void;
  compareBasis: KpiCompareBasis;
  onChangeCompare: (v: KpiCompareBasis) => void;
  dimension: string;
  onChangeDimension: (v: string) => void;
  priceSpec: string;
  onChangePriceSpec: (v: string) => void;
  priceSpecs: string[];
  revIncludeVat: boolean;
  onChangeRevVat: (v: boolean) => void;
  target: number;
  onChangeTarget: (v: number) => void;
  showSpark: boolean;
  onChangeShowSpark: (v: boolean) => void;
}) {
  const dimOptions = KPI_DIMENSION_OPTIONS[kpiIndex] ?? [];
  const isRevenue = kpiIndex === 1;
  const showTarget = kpiIndex === 2 || kpiIndex === 3; // 평균단가 / 가동차량
  // 추이(sparkline/증감비교)가 있는 KPI — 출하/매출/평균단가/가동차량/수금(0~4).
  // 미수/관급잔량/금일진척률(5~7)은 스냅샷·당일 단일이라 추이 없음.
  const hasTrend = kpiIndex <= 4;
  // 설정 항목이 전혀 없는 스냅샷/당일 KPI는 안내만 표시
  const noOptions = !hasTrend && dimOptions.length === 0 && !isRevenue && !showTarget;

  if (noOptions) {
    return (
      <div className="ws-data-block">
        <div className="ws-data-hint">
          ※ 이 지표는 스냅샷(또는 당일) 데이터라 별도 설정이 없습니다. 카드에 최신 값이 자동 표시됩니다.
        </div>
      </div>
    );
  }

  return (
    <div className="ws-data-block">
      {/* 차원 */}
      {dimOptions.length > 0 && (
        <div className="ws-sub-block">
          <div className="ws-sub-title">표시 항목</div>
          <Select
            value={dimension}
            options={dimOptions}
            onChange={onChangeDimension}
          />
          {/* 평균단가 — 규격(mixSpec) 선택. 전체 또는 특정 규격의 평균단가만 표시 */}
          {kpiIndex === 2 && (
            <div className="ws-sub-block" style={{ marginTop: 10 }}>
              <div className="ws-sub-title">규격</div>
              <Select
                value={priceSpec}
                options={[
                  { value: "", label: "전체 규격" },
                  ...priceSpecs.map((s) => ({ value: s, label: s })),
                ]}
                onChange={onChangePriceSpec}
              />
              <div className="ws-data-hint">
                ※ 특정 규격을 고르면 그 규격의 평균단가(Σ공급가/Σ수량)만 집계합니다. 표시 항목(관급/사급)과 함께 적용됩니다.
              </div>
            </div>
          )}
        </div>
      )}

      {/* sparkline 기간 */}
      {hasTrend && (
      <div className="ws-sub-block">
        <div className="ws-sub-title">추이 기간 (sparkline)</div>
        <div className="ws-period-grid">
          {PERIOD_OPTIONS.map((opt) => {
            const active = period === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                className={`ws-period-card ${active ? "is-active" : ""}`}
                onClick={() => onChangePeriod(opt.key)}
                aria-pressed={active ? "true" : "false"}
              >
                <div className="ws-period-label">{opt.label}</div>
                <div className="ws-period-desc">{opt.desc}</div>
              </button>
            );
          })}
        </div>
      </div>
      )}

      {/* 비교 기준 */}
      {hasTrend && (
      <div className="ws-sub-block">
        <div className="ws-sub-title">증감 비교 기준</div>
        <Select
          value={compareBasis}
          options={KPI_COMPARE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          onChange={(v) => onChangeCompare(v as KpiCompareBasis)}
        />
        <div className="ws-data-hint">
          ※ 카드 우상단 ▲▼ 증감률의 비교 대상입니다.
        </div>
      </div>
      )}

      {/* 매출 — VAT 토글 */}
      {isRevenue && (
        <div className="ws-sub-block">
          <div className="ws-sub-title">매출 기준</div>
          <div className="ws-toggle-list">
            <label className="ws-toggle-row">
              <input
                type="checkbox"
                checked={revIncludeVat}
                onChange={(e) => onChangeRevVat(e.target.checked)}
              />
              <span className="ws-toggle-label">
                <span>부가세(VAT) 포함</span>
                <span className="ws-toggle-desc">체크 해제 시 공급가액 기준</span>
              </span>
            </label>
          </div>
        </div>
      )}

      {/* 목표값 */}
      {showTarget && (
        <div className="ws-sub-block">
          <div className="ws-sub-title">목표값 {kpiIndex === 2 ? "(원/㎥)" : "(대)"}</div>
          <input
            type="number"
            className="ws-num-input"
            value={target || ""}
            min={0}
            placeholder="미사용 (0)"
            onChange={(e) => onChangeTarget(Math.max(0, Number(e.target.value) || 0))}
          />
          <div className="ws-data-hint">
            ※ 0이면 목표 표시 안 함. 설정 시 카드 하단에 목표 대비 % 표시.
          </div>
        </div>
      )}

      {/* sparkline 표시 토글 */}
      {hasTrend && (
      <div className="ws-sub-block">
        <div className="ws-toggle-list">
          <label className="ws-toggle-row">
            <input
              type="checkbox"
              checked={showSpark}
              onChange={(e) => onChangeShowSpark(e.target.checked)}
            />
            <span className="ws-toggle-label">
              <span>추이 그래프 표시</span>
              <span className="ws-toggle-desc">카드 하단 sparkline (끄면 숫자만 강조)</span>
            </span>
          </label>
        </div>
      </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 섹션 — 캘린더 (출하 표시 / 직원 생일 표시 토글)
// ─────────────────────────────────────────────────────────────
function CalendarSection({
  showShipments,
  onChangeShowShipments,
  showBirthdays,
  onChangeShowBirthdays,
}: {
  showShipments: boolean;
  onChangeShowShipments: (v: boolean) => void;
  showBirthdays: boolean;
  onChangeShowBirthdays: (v: boolean) => void;
}) {
  return (
    <div className="ws-data-block">
      <div className="ws-sub-block">
        <div className="ws-sub-title">표시 항목</div>
        <div className="ws-toggle-list">
          <label className="ws-toggle-row">
            <input
              type="checkbox"
              checked={showShipments}
              onChange={(e) => onChangeShowShipments(e.target.checked)}
            />
            <span className="ws-toggle-label">
              <span>출하 표시</span>
              <span className="ws-toggle-desc">
                출하 있는 날 날짜에 원(○)과 그 아래 물량(㎥)을 작게 표시
              </span>
            </span>
          </label>
          <label className="ws-toggle-row">
            <input
              type="checkbox"
              checked={showBirthdays}
              onChange={(e) => onChangeShowBirthdays(e.target.checked)}
            />
            <span className="ws-toggle-label">
              <span>직원 생일 표시</span>
              <span className="ws-toggle-desc">
                생일인 직원이 있는 날에 🎂 마커 (관리자 권한 필요 · 추후 상세 예정)
              </span>
            </span>
          </label>
        </div>
      </div>
      <div className="ws-data-hint">
        ※ 출하량은 일별 실제 출하 집계(daily_shipments) 기준입니다. 표시 중인 달만 조회합니다.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 섹션 — 분석 (자동 추천 + 축 모드. 수동 축 할당은 고급 설정 안에)
// "데이터 특성에 맞는 시각화"를 추천하고, 사용자는 한 번에 적용 가능.
// ─────────────────────────────────────────────────────────────

function AnalysisSection({
  mode,
  onChangeMode,
  candidates,
  assignment,
  onChangeAxis,
  recommendations,
}: {
  mode: YAxisMode;
  onChangeMode: (m: YAxisMode) => void;
  candidates: { code: string; label: string }[];
  assignment: Record<string, "left" | "right">;
  onChangeAxis: (code: string, axis: "left" | "right") => void;
  recommendations: Recommendation[];
}) {
  const [advanced, setAdvanced] = useState(mode !== "auto");

  return (
    <div className="ws-axis-block">
      {/* 1. Smart Recommendation 패널 */}
      <div className="ws-sub-block">
        <div className="ws-sub-title">
          <Icon name="help" size={14} />
          데이터 기반 추천
        </div>
        {recommendations.length === 0 ? (
          <div className="ws-reco-empty">
            현재 데이터로는 별도 추천이 없습니다. 기본 자동 설정으로 충분합니다.
          </div>
        ) : (
          <div className="ws-reco-list">
            {recommendations.map((r, i) => (
              <div key={i} className="ws-reco-card">
                <div className="ws-reco-msg">{r.message}</div>
                {r.apply && (
                  <button
                    type="button"
                    className="ws-reco-apply"
                    onClick={r.apply.onApply}
                  >
                    {r.apply.label}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 2. Y축 모드 — auto가 기본 + 고급 설정 토글 */}
      <div className="ws-sub-block">
        <div className="ws-sub-title">
          축 모드
          <button
            type="button"
            className="ws-advanced-toggle"
            onClick={() => setAdvanced((a) => !a)}
          >
            {advanced ? "고급 설정 접기" : "고급 설정 펼치기"}
          </button>
        </div>

        {!advanced ? (
          <div className="ws-axis-auto">
            <strong>자동</strong> 모드로 동작합니다. 데이터에 맞춰 단일 축 + 필요 시 추천 배너 표시.
          </div>
        ) : (
          <>
            <div className="ws-period-grid">
              {Y_AXIS_OPTIONS.filter((opt) => opt.advanced).map((opt) => {
                const active = mode === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    className={`ws-period-card ${active ? "is-active" : ""}`}
                    onClick={() => onChangeMode(opt.key)}
                    aria-pressed={active ? "true" : "false"}
                  >
                    <div className="ws-period-label">{opt.label}</div>
                    <div className="ws-period-desc">{opt.desc}</div>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="ws-advanced-back"
              onClick={() => onChangeMode("auto")}
            >
              ← 자동 모드로 되돌리기
            </button>

            {mode === "dual" && candidates.length > 0 && (
              <div className="ws-axis-assign">
                <div className="ws-axis-assign-head">
                  <span>데이터별 축 할당</span>
                  <span className="ws-axis-assign-hint">큰 값 그룹은 한쪽, 작은 값은 반대쪽으로</span>
                </div>
                <div className="ws-axis-assign-list">
                  {candidates.map(({ code, label }) => {
                    const axis = assignment[code] ?? "left";
                    return (
                      <div key={code} className="ws-axis-row">
                        <span className="ws-axis-row-label">{label}</span>
                        <div className="ws-axis-row-switch" role="radiogroup" aria-label={`${label} 축 선택`}>
                          <button
                            type="button"
                            className={`ws-axis-pill ${axis === "left" ? "is-active" : ""}`}
                            onClick={() => onChangeAxis(code, "left")}
                          >좌</button>
                          <button
                            type="button"
                            className={`ws-axis-pill ${axis === "right" ? "is-active" : ""}`}
                            onClick={() => onChangeAxis(code, "right")}
                          >우</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {mode === "normalize" && (
              <div className="ws-axis-info">
                ※ 각 데이터의 <strong>첫 유효값을 100</strong>으로 두고 이후 값을 % 비율로 환산합니다.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 섹션 — 시각화 (어떤 방식으로 그릴지)
// 업무 중심 라벨 (추세 / 부드러운 추세 / 누적 흐름 / 월별 비교 / 비교+합계 / 요약형)
// 기술명(techName)은 subtitle에 작게 표시.
// ─────────────────────────────────────────────────────────────

function VisualizationSection({
  value,
  onChange,
  widgetType,
}: {
  value: WidgetStylePreset;
  onChange: (v: WidgetStylePreset) => void;
  widgetType: string;
}) {
  return (
    <div className="ws-style-block">
      <div className="ws-style-grid">
        {STYLE_PRESETS.map((p) => {
          const compatible = !p.compat || p.compat.includes(widgetType as never);
          const active = value === p.key;
          return (
            <button
              key={p.key}
              type="button"
              className={`ws-style-card ${active ? "is-active" : ""} ${!compatible ? "is-disabled" : ""}`}
              onClick={() => compatible && onChange(p.key)}
              disabled={!compatible}
              aria-pressed={active ? "true" : "false"}
              title={!compatible ? "현재 위젯 type과 호환되지 않음" : undefined}
            >
              <div className="ws-style-thumb">
                <PresetThumb preset={p.key} />
              </div>
              <div className="ws-style-meta">
                <div className="ws-style-label">
                  {p.label}
                  <span className="ws-style-tech">{p.techName}</span>
                </div>
                <div className="ws-style-desc">{p.desc}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 섹션 — 스타일 (인터랙션 토글 / 향후 색상 팔레트 등)
// ─────────────────────────────────────────────────────────────

function StyleSection({
  focusEnabled,
  onChangeFocus,
  autoSuggestEnabled,
  onChangeAutoSuggest,
}: {
  focusEnabled: boolean;
  onChangeFocus: (v: boolean) => void;
  autoSuggestEnabled: boolean;
  onChangeAutoSuggest: (v: boolean) => void;
}) {
  return (
    <div className="ws-style-block">
      <div className="ws-toggle-list">
        <label className="ws-toggle-row">
          <input
            type="checkbox"
            checked={focusEnabled}
            onChange={(e) => onChangeFocus(e.target.checked)}
          />
          <span className="ws-toggle-label">
            <span>Focus 모드</span>
            <span className="ws-toggle-desc">범례에 마우스 올리면 해당 데이터만 강조 (나머지는 흐리게)</span>
          </span>
        </label>
        <label className="ws-toggle-row">
          <input
            type="checkbox"
            checked={autoSuggestEnabled}
            onChange={(e) => onChangeAutoSuggest(e.target.checked)}
          />
          <span className="ws-toggle-label">
            <span>자동 추천 표시</span>
            <span className="ws-toggle-desc">데이터 특성에 맞는 시각화 추천 표시 (분석 섹션 + 차트 위 배너)</span>
          </span>
        </label>
      </div>
      <div className="ws-future-hint">
        ※ 색상 팔레트 · 폰트 · 라벨 위치 등은 향후 추가 예정.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 섹션 — 인사이트 (향후 자동 이상 감지 · 변화 알림 placeholder)
// ─────────────────────────────────────────────────────────────

function InsightSection() {
  return (
    <div className="ws-insight-block">
      <div className="ws-insight-intro">
        운영 데이터에서 <strong>이상 감지</strong>와 <strong>빠른 해석</strong>을 자동으로 표시할 예정입니다.
        현재는 미리보기 — 향후 차트 위에 badge 형태로 노출됩니다.
      </div>
      <div className="ws-insight-list">
        {INSIGHT_TYPES.map((t) => (
          <div key={t.key} className="ws-insight-card is-future">
            <div className="ws-insight-label">{t.label}</div>
            <div className="ws-insight-desc">{t.desc}</div>
            <span className="ws-insight-badge">예정</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** 프리셋별 미니 SVG 미리보기 */
function PresetThumb({ preset }: { preset: WidgetStylePreset }) {
  const c = "var(--c-primary-600)";
  const c2 = "var(--c-accent)";
  switch (preset) {
    case "basic-line":
      return (
        <svg viewBox="0 0 60 30" width="60" height="30">
          <polyline points="2,22 12,18 22,14 32,20 42,10 52,12 58,6" fill="none" stroke={c} strokeWidth="1.5" />
        </svg>
      );
    case "smooth-line":
      return (
        <svg viewBox="0 0 60 30" width="60" height="30">
          <path d="M2 22 Q12 14 22 16 T42 10 T58 6" fill="none" stroke={c} strokeWidth="1.5" />
        </svg>
      );
    case "area-line":
      return (
        <svg viewBox="0 0 60 30" width="60" height="30">
          <path d="M2 22 L12 18 L22 14 L32 20 L42 10 L52 12 L58 6 L58 28 L2 28 Z" fill={c} fillOpacity="0.2" stroke={c} strokeWidth="1.2" />
        </svg>
      );
    case "bar":
      return (
        <svg viewBox="0 0 60 30" width="60" height="30">
          {[6, 16, 24, 12, 20, 14, 22].map((h, i) => (
            <rect key={i} x={2 + i * 8} y={28 - h} width="5" height={h} fill={c} />
          ))}
        </svg>
      );
    case "mixed":
      return (
        <svg viewBox="0 0 60 30" width="60" height="30">
          {[10, 16, 12, 20, 14].map((h, i) => (
            <rect key={i} x={4 + i * 11} y={28 - h} width="7" height={h} fill={c} fillOpacity="0.6" />
          ))}
          <polyline points="7,15 18,10 29,18 40,8 51,12" fill="none" stroke={c2} strokeWidth="1.5" />
        </svg>
      );
    case "minimal":
      return (
        <svg viewBox="0 0 60 30" width="60" height="30">
          <polyline points="4,20 18,15 32,17 46,8 56,11" fill="none" stroke={c} strokeWidth="1.5" />
        </svg>
      );
  }
}

function EmptyHint({ text }: { text: string }) {
  return (
    <div style={{ padding: "32px 8px", color: "var(--c-text-muted)", fontSize: "var(--fs-base)", textAlign: "center" }}>
      <Icon name="help" size={20} />
      <div style={{ marginTop: 8 }}>{text}</div>
    </div>
  );
}
